import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import  { KnowledgeGraphLink } from '../../shared/models/knowledge-graph';
import { LexiconService } from '../../shared/services/lexicon.service';
import { LampUpdateResponse } from '../../shared/models/common';
import { BehaviorSubject, Observable } from 'rxjs';
import { Lexeme } from '../../shared/models/lexicon';
import { KnowledgeGraphService } from '../../shared/services/knowledge-graph.service';
import { MatSnackBar } from '@angular/material';
import { TranslateService } from '@ngx-translate/core';
import { SessionService } from '../../shared/services/session.service';

@Component({
  selector: 'app-changeable-list',
  templateUrl: './changeable-list.component.html',
  styleUrls: ['./changeable-list.component.less']
})
export class ChangeableListComponent implements OnChanges {
  @Input() list: KnowledgeGraphLink[];
  @Input() isAbleToReplaceLinks: boolean;
  @Input() isDomainList: boolean;
  @Input() isAdmin: boolean;
  @Input() isLexemesDialog: boolean;
  @Input() isFamilyDialog: boolean;
  @Input() representativeLemma: string;
  @Input() header: string;
  @Input() familyId: number;

  public commaDelimitedIdsList: string = '';
  public isLoading: boolean = false;

  @Output() addSilentDomainsEvent: EventEmitter<string> = new EventEmitter<string>();
  @Output() addGenerationDomainsEvent: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() refreshFamilyEvent: EventEmitter<void> = new EventEmitter<void>();
  @Output() deleteLinksEvent: EventEmitter<string> = new EventEmitter<string>();
  @Output() moveLinksEvent: EventEmitter<{ commaDelimitedIdsList: string, commaDelimitedListOfTargetFamilies: string }> =
    new EventEmitter<{ commaDelimitedIdsList: string, commaDelimitedListOfTargetFamilies: string }>();

  @Output() addLinksEvent: EventEmitter<string> = new EventEmitter<string>();
  @Output() checkedFamiliesEvent: EventEmitter<{linkId: number, isLinkChecked: boolean, description: string}> =
    new EventEmitter<{linkId: number, isLinkChecked: boolean, description: string}>();

  public errorMessageBehaviorSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public errorMessageSource: Observable<string> = this.errorMessageBehaviorSubject.asObservable();
  

  constructor(private lexiconService: LexiconService,
              private snackBar: MatSnackBar,
              private translateService: TranslateService,
              private sessionService: SessionService) {
  }

  // Autocheck history is kept per family and only the lexemes list shows the autocheck markers.
  // Admin only: the backend rejects non-admins for language-independent tables such as Families.
  public get isAbleToClearAutocheckHistory(): boolean {
    return !!this.isLexemesDialog && !!this.familyId && !!this.sessionService.isAdmin();
  }

  ngOnChanges(): void {
    this.commaDelimitedIdsList = '';
  }

  public handleLink(linkId: number, isLinkChecked: boolean, description: string) {
    const linksArray: string[] = this.commaDelimitedIdsList ? (this.commaDelimitedIdsList.split(',')) : [];

    this.checkedFamiliesEvent.next({linkId: linkId, isLinkChecked: isLinkChecked, description: description});

    if (isLinkChecked) {
      linksArray.push(linkId.toString());
    } else {
      const deletedLexemeId = linksArray.findIndex(element => (element.toString() === linkId.toString()));
      linksArray.splice(deletedLexemeId, 1);
    }

    if (linksArray.length) {
      this.commaDelimitedIdsList = linksArray.join(',');
    } else {
      this.commaDelimitedIdsList = '';
    }
  }

  public deleteLinksAfterUserConfirmation(doesUserConfirmDeletion: boolean) {
    if (doesUserConfirmDeletion) {
      this.deleteLinksEvent.next(this.commaDelimitedIdsList);
    }
  }

  public replaceLinks(familyIdWhereListMovedTo: string) {
    if (familyIdWhereListMovedTo && this.commaDelimitedIdsList) {
      this.moveLinksEvent.next({
        commaDelimitedIdsList: this.commaDelimitedIdsList,
        commaDelimitedListOfTargetFamilies: familyIdWhereListMovedTo
      });
    }
  }

  public updateLinksList(commaDelimitedIdsListToAdd: string) {
    if(commaDelimitedIdsListToAdd == '-1') {
      this.refreshFamilyEvent.emit();
      return;
    }
    if (commaDelimitedIdsListToAdd) {
      this.addLinksEvent.next(commaDelimitedIdsListToAdd);
    }
  }

  public addSilentDomains(event) {
    this.addSilentDomainsEvent.emit(event);
  }

  public addGeneratedDomains(event) {
    if(event) {
      this.addGenerationDomainsEvent.emit(event);
    }
  }

  public approveLexemeFamily(lexeme: Lexeme, familyId: number): void {
    this.isLoading = true;
    this.lexiconService.approveLexemeFamily(lexeme.id, familyId)
    .then((message: LampUpdateResponse) => {
      this.isLoading = false;
      if (message.success) {
        lexeme.unverified = 'verified';
        this.clearTheErrorMessage();
      } else {
        this.showErrorMessage(message.error);
      }
    });
  }

  public switchSilent(link: KnowledgeGraphLink) {
    this.isLoading = true;
    this.lexiconService.toggleSilent(this.familyId, link.id)
    .then((message: LampUpdateResponse) => {
      this.isLoading = false;
      if (message.success) {
        link.discouraged = !link.discouraged;
        this.clearTheErrorMessage();
      } else {
        this.showErrorMessage(message.error);
      }
    });
  }

  public propagateDomain(link: KnowledgeGraphLink) {
    this.isLoading = true;
    this.lexiconService.propagateDomain(this.familyId, link.id)
    .then((message: LampUpdateResponse) => {
      this.isLoading = false;
      if (message.success) {
        this.clearTheErrorMessage();
        this.snackBar.open(this.translateService.instant('knowledge-graph.propagate-domain-success'), 'Close', {
          duration: 2000
        });
      } else {
        this.showErrorMessage(message.error);
      }
    });
  }

  public clearAutocheckHistory() {
    this.isLoading = true;
    this.lexiconService.clearAutocheckHistory('Families', this.familyId)
    .then((message: LampUpdateResponse) => {
      this.isLoading = false;
      // the web method returns an empty body on success, so only an explicit failure is an error
      if (message && message.success === false) {
        this.showErrorMessage(message.error);
      } else {
        this.clearTheErrorMessage();
        this.snackBar.open(this.translateService.instant('knowledge-graph.clear-autocheck-history-success'), 'Close', {
          duration: 2000
        });
      }
    });
  }

  public showErrorMessage(error?: string): void {
    this.errorMessageBehaviorSubject.next(error);
  }

  public clearTheErrorMessage(): void {
    this.showErrorMessage('');
  }

  getUnverifiedEmoji(unverified: 'verified' | 'failedAutoCheck' | 'autogenerated' | 'humanFactor' | 'autoGrammar' | 'autoStyle') {
    switch(unverified) {
      case 'autogenerated':
      case 'autoGrammar':
      case 'autoStyle':
        return '💡';
      case 'failedAutoCheck':
        return '❗';
      default:
        return '🩺';
    }
  }
}
